import { ArrowLeft, Save, UserPlus, Users } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CustomerSelector } from "@/components/projects/CustomerSelector";
import { AustralianLocationSelector } from "@/components/ui/australian-location-selector";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { cloudflare } from "@/integrations/cloudflare/client";
import { devLog, logError } from "@/lib/env";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  created_at: string;
}

interface ProjectFormData {
  projectName: string;
  customerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  budget: string;
  timeframe: string;
  description: string;
}

type CustomerInputMode = "existing" | "new";

interface ProjectFormProps {
  onBack: () => void;
  onSubmit: (projectId: string, projectData: ProjectFormData) => void;
  editingProject?: {
    id: string;
    project_name?: string;
    customer_name?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    country?: string;
    budget?: string;
    timeframe?: string;
    description?: string;
    customer_id?: number;
  };
  isEditMode?: boolean;
}

export function ProjectForm({
  onBack,
  onSubmit,
  editingProject,
  isEditMode = false,
}: ProjectFormProps) {
  const [searchParams] = useSearchParams();
  const baseId = useId();
  const fieldIds = useMemo(
    () => ({
      customerName: `${baseId}-customer-name`,
      email: `${baseId}-email`,
      phone: `${baseId}-phone`,
      zipCode: `${baseId}-zip-code`,
      address: `${baseId}-address`,
      projectName: `${baseId}-project-name`,
      description: `${baseId}-description`,
    }),
    [baseId]
  );
  const [customerMode, setCustomerMode] = useState<CustomerInputMode>("existing");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [formData, setFormData] = useState<ProjectFormData>({
    projectName: editingProject?.project_name || "",
    customerName: editingProject?.customer_name || "",
    email: editingProject?.email || "",
    phone: editingProject?.phone || "",
    address: editingProject?.address || "",
    city: editingProject?.city || "",
    state: editingProject?.state || "",
    zip_code: editingProject?.zip_code || "",
    country: editingProject?.country || "Australia",
    budget: editingProject?.budget || "",
    timeframe: editingProject?.timeframe || "",
    description: editingProject?.description || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch existing customer data in edit mode
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (isEditMode && editingProject?.customer_id && !selectedCustomer) {
        setIsLoadingCustomer(true);
        try {
          const response = await fetch(
            `${cloudflare.apiUrl}/api/v1/customers/${editingProject.customer_id}`
          );
          if (response.ok) {
            const result = await response.json();
            const customerData = result.data;
            setSelectedCustomer({
              id: customerData.id,
              first_name: customerData.first_name,
              last_name: customerData.last_name,
              email: customerData.email,
              phone: customerData.phone || "",
              address: customerData.address || "",
              city: customerData.city || "",
              state: customerData.state || "",
              zip_code: customerData.zip_code || "",
              country: customerData.country || "",
              created_at: customerData.created_at,
            });
            setCustomerMode("existing");
          }
        } catch (error) {
          logError("Error fetching customer data", error instanceof Error ? error : undefined);
        } finally {
          setIsLoadingCustomer(false);
        }
      }
    };
    fetchCustomerData();
  }, [isEditMode, editingProject?.customer_id, selectedCustomer]);

  // Pre-select customer from URL query param
  useEffect(() => {
    const fetchCustomerFromUrl = async () => {
      const customerId = searchParams.get("customer_id");
      if (customerId && !isEditMode && !selectedCustomer) {
        setIsLoadingCustomer(true);
        try {
          const response = await fetch(`${cloudflare.apiUrl}/api/v1/customers/${customerId}`);
          if (response.ok) {
            const result = await response.json();
            const customerData = result.data;
            const customer: Customer = {
              id: customerData.id,
              first_name: customerData.first_name,
              last_name: customerData.last_name,
              email: customerData.email,
              phone: customerData.phone || "",
              address: customerData.address || "",
              city: customerData.city || "",
              state: customerData.state || "",
              zip_code: customerData.zip_code || "",
              country: customerData.country || "",
              created_at: customerData.created_at,
            };
            setSelectedCustomer(customer);
            setCustomerMode("existing");
            // Populate form data from selected customer
            setFormData((prev) => ({
              ...prev,
              customerName: `${customer.first_name} ${customer.last_name}`,
              email: customer.email,
              phone: customer.phone || "",
              address: customer.address || "",
              city: customer.city || "",
              state: customer.state || "",
              zip_code: customer.zip_code || "",
              country: customer.country || "",
            }));
            devLog("Pre-selected customer from URL:", {
              customerId: customer.id,
              name: `${customer.first_name} ${customer.last_name}`,
            });
          }
        } catch (error) {
          logError("Error fetching customer from URL", error instanceof Error ? error : undefined);
        } finally {
          setIsLoadingCustomer(false);
        }
      }
    };
    fetchCustomerFromUrl();
  }, [searchParams, isEditMode, selectedCustomer]);

  const handleChange = (field: keyof ProjectFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode && editingProject) {
        // Update existing project
        const updateData = {
          project_name:
            formData.projectName || `${formData.customerName} - ${new Date().toLocaleDateString()}`,
          customer_name: formData.customerName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          country: formData.country || "Australia",
          budget: formData.budget,
          timeframe: formData.timeframe,
          description: formData.description,
        };

        const projectResponse = await fetch(
          `${cloudflare.apiUrl}/api/v1/projects/${editingProject.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updateData),
          }
        );

        if (!projectResponse.ok) {
          const errorData = await projectResponse.json();
          throw new Error(errorData.error || "Failed to update project");
        }

        await projectResponse.json();

        toast({
          title: "Project Updated",
          description: `Project "${formData.projectName || formData.customerName}" has been updated successfully.`,
        });

        onSubmit(editingProject.id, formData);
        return;
      }

      // Create new project logic
      let customerId = null;
      let customerEmail = "";
      let customerName = "";

      if (customerMode === "existing" && selectedCustomer) {
        // Use existing customer
        customerId = selectedCustomer.id;
        customerEmail = selectedCustomer.email;
        customerName = `${selectedCustomer.first_name} ${selectedCustomer.last_name}`;
        devLog("Using existing customer:", { customerId, customerEmail, customerName });
      } else if (customerMode === "new") {
        // Create new customer first
        devLog("Creating new customer with data:", formData);

        const customerData = {
          first_name: formData.customerName.split(" ")[0] || formData.customerName,
          last_name: formData.customerName.split(" ").slice(1).join(" ") || "",
          email: formData.email,
          phone: formData.phone || null,
          address: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zip_code || null,
          country: formData.country || "Australia", // Default to USA if not specified
        };

        devLog("Sending customer data to API:", customerData);

        const customerResponse = await fetch(`${cloudflare.apiUrl}/api/v1/customers`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(customerData),
        });

        if (!customerResponse.ok) {
          const errorData = await customerResponse.json();
          const errorMessage = typeof errorData.error === "string" ? errorData.error : undefined;
          if (errorMessage) {
            devLog("Customer creation error payload:", errorData);
          }
          logError("Customer creation failed", errorMessage ? new Error(errorMessage) : undefined);
          throw new Error(errorMessage || "Failed to create customer");
        }

        const customerResult = await customerResponse.json();
        devLog("Customer created successfully:", customerResult.data);

        const createdCustomer = customerResult.data;
        customerId = createdCustomer.id;
        customerEmail = createdCustomer.email;
        const createdCustomerName =
          `${createdCustomer.first_name} ${createdCustomer.last_name}`.trim();
        customerName = createdCustomerName || formData.customerName;

        // Show success message for customer creation
        toast({
          title: "Customer Created",
          description: `Customer "${customerName}" has been added to the database.`,
        });
      }

      // Create project with customer reference
      const projectData = {
        project_name:
          formData.projectName || `${customerName} - ${new Date().toLocaleDateString()}`,
        customer_id: customerId,
        email: customerEmail,
        customer_name: customerName,
        phone: customerMode === "existing" ? selectedCustomer?.phone : formData.phone,
        address: customerMode === "existing" ? selectedCustomer?.address : formData.address,
        city: customerMode === "existing" ? selectedCustomer?.city : formData.city,
        budget: formData.budget,
        timeframe: formData.timeframe,
        description: formData.description,
        status: "draft",
      };

      const projectResponse = await fetch(`${cloudflare.apiUrl}/api/v1/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(projectData),
      });

      if (!projectResponse.ok) {
        const errorData = await projectResponse.json();
        throw new Error(errorData.error || "Failed to create project");
      }

      const projectResult = await projectResponse.json();

      toast({
        title: "Success!",
        description:
          customerMode === "new"
            ? `✅ New customer "${customerName}" has been saved to the database.\n✅ Project #${projectResult.data.id} has been created successfully.`
            : `✅ Project #${projectResult.data.id} created for ${customerName}.`,
      });

      // Log success for debugging
      if (customerMode === "new") {
        devLog(
          `Successfully created customer ID ${customerId} and project ID ${projectResult.data.id}`
        );
      }

      onSubmit(projectResult.data.id, { ...formData, customerName });
    } catch (error) {
      logError("Error creating project", error instanceof Error ? error : undefined);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to create project. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle customer selection from existing customers
  const handleCustomerSelect = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      // Populate form data from selected customer
      setFormData((prev) => ({
        ...prev,
        customerName: `${customer.first_name} ${customer.last_name}`,
        email: customer.email,
        phone: customer.phone || "",
        address: customer.address || "",
        city: customer.city || "",
        state: customer.state || "",
        zip_code: customer.zip_code || "",
        country: customer.country || "",
      }));
    }
  };

  // Handle switching to new customer mode
  const handleCreateNewCustomer = () => {
    setCustomerMode("new");
    setSelectedCustomer(null);
  };

  // Handle location selection for new customers
  const handleLocationChange = (field: "country" | "state" | "city", value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Validation based on customer mode
  const isFormValid = (() => {
    if (customerMode === "existing") {
      return selectedCustomer !== null;
    }
    // For new customers, require basic info only
    return formData.customerName && formData.email && formData.phone && formData.address;
  })();

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl border shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack} className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              {isEditMode ? "Edit Renovation Project" : "New Renovation Project"}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              {isEditMode
                ? "Update project information and customer details"
                : "Create a new renovation project and manage customer information"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="shadow-md border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl sm:text-2xl flex items-center gap-2">
              <Save className="w-5 h-5 text-primary" />
              {isEditMode ? "Update Project Details" : "Project Details"}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              {isEditMode
                ? "Update the project information and customer details below"
                : "Fill in the project information and customer details below"}
            </p>
          </CardHeader>
          <CardContent className="space-y-6 sm:space-y-8">
            {/* Customer Information */}
            <div className="space-y-4 sm:space-y-6">
              {isEditMode ? (
                /* Simple Customer Display for Edit Mode */
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Customer</h3>
                      {selectedCustomer ? (
                        <p className="text-sm text-muted-foreground mt-1">
                          {selectedCustomer.first_name} {selectedCustomer.last_name} •{" "}
                          {selectedCustomer.email}
                        </p>
                      ) : isLoadingCustomer ? (
                        <p className="text-sm text-muted-foreground mt-1">Loading customer...</p>
                      ) : (
                        <p className="text-sm text-muted-foreground mt-1">No customer selected</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCustomerSelector(true)}
                    >
                      Change Customer
                    </Button>
                  </div>
                </div>
              ) : (
                /* Full Customer Selection for Create Mode */
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 sm:p-6 border">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-1">
                        Customer Information
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Choose an existing customer or create a new one
                      </p>
                    </div>
                    <div className="flex gap-2 self-start sm:self-auto">
                      <Button
                        type="button"
                        variant={customerMode === "existing" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCustomerMode("existing")}
                        className="flex items-center gap-2 flex-1 sm:flex-none"
                      >
                        <Users className="h-4 w-4" />
                        <span className="hidden xs:inline">Existing</span>
                        <span className="xs:hidden">Existing</span>
                      </Button>
                      <Button
                        type="button"
                        variant={customerMode === "new" ? "default" : "outline"}
                        size="sm"
                        onClick={handleCreateNewCustomer}
                        className="flex items-center gap-2 flex-1 sm:flex-none"
                      >
                        <UserPlus className="h-4 w-4" />
                        <span className="hidden xs:inline">New Customer</span>
                        <span className="xs:hidden">New</span>
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer Selector or New Customer Form */}
              {((customerMode === "existing" && !isEditMode) ||
                (isEditMode && showCustomerSelector)) && (
                /* Customer Selection */
                <div>
                  <CustomerSelector
                    selectedCustomer={selectedCustomer}
                    onCustomerSelect={(customer) => {
                      handleCustomerSelect(customer);
                      if (isEditMode) {
                        setShowCustomerSelector(false);
                      }
                    }}
                    onCreateNewCustomer={handleCreateNewCustomer}
                    hideNewCustomerButton={isEditMode}
                  />
                  {isEditMode && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCustomerSelector(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {!isEditMode && customerMode === "new" && (
                /* New Customer Form - Only in Create Mode */
                <Card className="border-2 border-dashed border-blue-200 bg-blue-50/30">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                      <UserPlus className="h-5 w-5 text-blue-600" />
                      New Customer Details
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-2">
                      This customer will be saved to your database for future projects
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      <div className="space-y-2">
                        <Label htmlFor={fieldIds.customerName} className="text-sm font-medium">
                          Customer Name *
                        </Label>
                        <Input
                          id={fieldIds.customerName}
                          value={formData.customerName}
                          onChange={(e) => handleChange("customerName", e.target.value)}
                          placeholder="Enter full customer name"
                          required={true}
                          className="h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={fieldIds.email} className="text-sm font-medium">
                          Email Address *
                        </Label>
                        <Input
                          id={fieldIds.email}
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="customer@email.com"
                          required={true}
                          className="h-11"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      <div className="space-y-2">
                        <Label htmlFor={fieldIds.phone} className="text-sm font-medium">
                          Phone Number *
                        </Label>
                        <Input
                          id={fieldIds.phone}
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          placeholder="+64 21 123 4567"
                          required={true}
                          className="h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={fieldIds.zipCode} className="text-sm font-medium">
                          Postcode
                        </Label>
                        <Input
                          id={fieldIds.zipCode}
                          value={formData.zip_code}
                          onChange={(e) => handleChange("zip_code", e.target.value)}
                          placeholder="3000"
                          maxLength={4}
                          className="h-11"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={fieldIds.address} className="text-sm font-medium">
                        Street Address *
                      </Label>
                      <Input
                        id={fieldIds.address}
                        value={formData.address}
                        onChange={(e) => handleChange("address", e.target.value)}
                        placeholder="123 Main Street, Unit 4A"
                        required={true}
                        className="h-11"
                      />
                    </div>

                    <AustralianLocationSelector
                      state={formData.state}
                      city={formData.city}
                      onStateChange={(value) => handleLocationChange("state", value)}
                      onCityChange={(value) => handleLocationChange("city", value)}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Project Location - only show if using existing customer */}
            {customerMode === "existing" && selectedCustomer && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 sm:p-6 border border-green-200">
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-3">
                    Project Location
                  </h3>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="space-y-1 flex-1">
                      <p className="text-sm font-medium text-green-800">Using customer address:</p>
                      <p className="text-sm text-green-700 font-medium">
                        {selectedCustomer.address && `${selectedCustomer.address}, `}
                        {selectedCustomer.city &&
                          `${selectedCustomer.city}${selectedCustomer.state ? `, ${selectedCustomer.state}` : ""}`}
                        {selectedCustomer.country && ` - ${selectedCustomer.country}`}
                      </p>
                    </div>
                    <Badge
                      variant="secondary"
                      className="bg-green-100 text-green-700 self-start sm:self-auto"
                    >
                      From Customer Profile
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            {/* Project Details */}
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 sm:p-6 border border-purple-200">
                <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                  Project Specifications
                </h3>
                <p className="text-sm text-muted-foreground">
                  Define the scope, budget, and timeline for this renovation project
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor={fieldIds.projectName} className="text-sm font-medium">
                  Project Name
                </Label>
                <Input
                  id={fieldIds.projectName}
                  value={formData.projectName}
                  onChange={(e) => handleChange("projectName", e.target.value)}
                  placeholder="e.g., Master Bathroom Renovation, Guest Bath Update..."
                  className="h-11"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to auto-generate from customer name and date
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <Label htmlFor="budget" className="text-sm font-medium">
                    Budget Range (Optional)
                  </Label>
                  <Select
                    value={formData.budget}
                    onValueChange={(value) => handleChange("budget", value)}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select budget range (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not-specified">Not specified</SelectItem>
                      <SelectItem value="under-10k">Under $10,000</SelectItem>
                      <SelectItem value="10k-20k">$10,000 - $20,000</SelectItem>
                      <SelectItem value="20k-35k">$20,000 - $35,000</SelectItem>
                      <SelectItem value="35k-50k">$35,000 - $50,000</SelectItem>
                      <SelectItem value="over-50k">Over $50,000</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timeframe" className="text-sm font-medium">
                    Expected Timeframe (Optional)
                  </Label>
                  <Select
                    value={formData.timeframe}
                    onValueChange={(value) => handleChange("timeframe", value)}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select timeframe (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not-specified">Not specified</SelectItem>
                      <SelectItem value="asap">ASAP</SelectItem>
                      <SelectItem value="1-month">Within 1 month</SelectItem>
                      <SelectItem value="2-3-months">2-3 months</SelectItem>
                      <SelectItem value="3-6-months">3-6 months</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={fieldIds.description} className="text-sm font-medium">
                  Project Description
                </Label>
                <Textarea
                  id={fieldIds.description}
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Describe the renovation requirements, current condition, special requests, materials preferences..."
                  rows={4}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  Optional: Provide additional details to help create accurate quotes
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-4 sm:p-6 border">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="bg-primary hover:bg-primary/90 h-12 text-base font-medium flex-1 sm:flex-none sm:px-8"
                  size="lg"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {isSubmitting
                    ? isEditMode
                      ? "Updating Project..."
                      : "Creating Project..."
                    : isEditMode
                      ? "Update Project"
                      : "Create Project"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  className="h-12 text-base sm:px-6"
                  size="lg"
                >
                  Cancel
                </Button>
              </div>
              {!isFormValid && (
                <p className="text-sm text-muted-foreground mt-3 text-center sm:text-left">
                  Please fill in all required fields to create the project
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
